import { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Tooltip } from '@mui/material';

// Component to show tooltip only when text is truncated
const EllipsisTooltip = ({
  children,
  title,
  style = {},
  // Opt-in: show the tooltip regardless of whether the visible text is
  // truncated — for callers whose `title` deliberately carries more detail
  // than the (short, non-truncated) displayed text, e.g. a condensed
  // multi-select summary like "All Projects".
  force = false,
  ...props
}) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  const checkOverflow = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    // A hidden element (collapsed section, inactive tab) reports clientWidth 0
    // against a non-zero scrollWidth, which would read as "overflowing" for
    // every value. Keep the last known answer until it's measurable.
    if (!el.clientWidth) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    const timer = setTimeout(checkOverflow, 0);
    window.addEventListener('resize', checkOverflow);

    const element = textRef.current;
    let resizeObserver;
    if (element && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(element);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
      resizeObserver?.disconnect();
    };
  }, [checkOverflow, children, title]);

  const typographyProps = {
    ref: textRef,
    variant: 'body2',
    // Re-measure on pointer entry, not just on mount and resize. The mount
    // measurement can be taken before the layout has settled (rows rendered
    // from async data, sticky-header tables) or before a web font swaps in and
    // widens the text — and because the ResizeObserver watches this element's
    // own box, which doesn't change in a fixed-layout table cell, it never
    // fires to correct the verdict. Anything that only started overflowing
    // after that first measurement would otherwise never get a tooltip. MUI
    // re-reads `title` on every render, so the tooltip still opens on the same
    // hover that triggered the re-measure.
    onMouseEnter: checkOverflow,
    sx: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      fontSize: 'inherit',
      ...style,
    },
  };

  // The Tooltip stays mounted whether or not the text currently overflows, and
  // an empty title is what suppresses it (MUI sets open = false for a falsy
  // title, re-read on every render), rather than swapping between a wrapped
  // and an unwrapped child. That's what lets the hover re-measure above
  // surface a tooltip on the very hover that discovered the overflow — with
  // the child unwrapped, MUI would have no hover listeners attached yet.
  // Extract PopperProps and slotProps to merge them properly
  const { PopperProps, slotProps, ...restProps } = props;

  const popperZIndex =
    PopperProps?.sx?.zIndex ??
    PopperProps?.style?.zIndex ??
    slotProps?.popper?.sx?.zIndex ??
    slotProps?.popper?.style?.zIndex ??
    10003;

  return (
    <Tooltip
      title={isOverflowing || force ? title || children : ''}
      arrow
      placement="top"
      disablePortal={false}
      {...restProps}
      PopperProps={{
        ...PopperProps,
        sx: {
          zIndex: popperZIndex,
          ...PopperProps?.sx,
        },
        style: {
          zIndex: popperZIndex,
          ...PopperProps?.style,
        },
      }}
      slotProps={{
        ...slotProps,
        popper: {
          ...slotProps?.popper,
          style: {
            zIndex: popperZIndex,
            ...slotProps?.popper?.style,
          },
        },
        // Show full content without a scrollbar; caller sx is spread last to allow overrides.
        tooltip: {
          ...slotProps?.tooltip,
          sx: {
            wordBreak: 'break-word',
            ...slotProps?.tooltip?.sx,
          },
        },
      }}
    >
      <Typography {...typographyProps}>{children}</Typography>
    </Tooltip>
  );
};

export default EllipsisTooltip;
